import { Box } from '@mui/material';
import type { Order, OrderItem } from '../../../api/order';
import { useAppContext } from '../../context/AppContext';
import { buildJobOrderSpec } from '../../utils/jobOrder';
import JobOrderHeader from './JobOrderHeader';
import JobOrderImage from './JobOrderImage';
import JobOrderSummary from './JobOrderSummary';
import MonitoringStrip from './MonitoringStrip';
import RemarksSection from './RemarksSection';
import SpecSection from './SpecSection';
import {
  PRINT_BORDER,
  PRINT_FONT_FAMILY,
  PRINT_IMAGE_COLUMN_WIDTH,
} from './printStyles';

interface JobOrderCardProps {
  order: Order;
  item: OrderItem;
}

const JobOrderCard = ({ order, item }: JobOrderCardProps) => {
  const { sizes, vestments } = useAppContext();
  const spec = buildJobOrderSpec({ order, item }, { sizes, vestments });

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 1fr) ${PRINT_IMAGE_COLUMN_WIDTH}px`,
        gridTemplateRows: 'auto minmax(0, 1fr) auto auto',
        gridTemplateAreas: `
          "header header"
          "specs image"
          "remarks remarks"
          "monitoring monitoring"
        `,
        flex: 1,
        minHeight: 0,
        border: PRINT_BORDER,
        fontFamily: PRINT_FONT_FAMILY,
        boxSizing: 'border-box',
        overflow: 'hidden',
        breakInside: 'avoid',
        '& .MuiTypography-root': { fontFamily: PRINT_FONT_FAMILY },
      }}
    >
      <Box sx={{ gridArea: 'header' }}>
        <JobOrderHeader dueDate={spec.dueDate} quantity={spec.quantity} />
      </Box>

      <Box
        sx={{
          gridArea: 'image',
          minHeight: 0,
          borderLeft: PRINT_BORDER,
          borderBottom: PRINT_BORDER,
        }}
      >
        <JobOrderImage
          imageUrl={spec.imageUrl}
          alt={spec.imageAlt}
          centerFinish={spec.centerFinish}
        />
      </Box>

      <Box
        sx={{
          gridArea: 'specs',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          borderBottom: PRINT_BORDER,
        }}
      >
        <Box sx={{ borderBottom: PRINT_BORDER }}>
          <JobOrderSummary
            customerName={spec.customerName}
            title={spec.title}
            designCode={spec.designCode}
            size={spec.size}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${spec.sections.length}, minmax(0, 1fr))`,
            gridTemplateRows: 'minmax(0, 1fr)',
            flex: '1 1 0px',
            height: 0,
            minHeight: 0,
            overflow: 'hidden',
            '& > :not(:first-of-type)': { borderLeft: PRINT_BORDER },
          }}
        >
          {spec.sections.map((section) => (
            <SpecSection key={section.title} section={section} />
          ))}
        </Box>
      </Box>

      <Box sx={{ gridArea: 'remarks', borderBottom: PRINT_BORDER }}>
        <RemarksSection remarks={spec.remarks} />
      </Box>

      <Box sx={{ gridArea: 'monitoring' }}>
        <MonitoringStrip />
      </Box>
    </Box>
  );
};

export default JobOrderCard;
